import React, { CSSProperties } from 'react';
import styled, { css, createGlobalStyle } from 'styled-components';
import { useField } from 'formik';
import * as LSTVGlobals from '~/globals';
import Autocomplete from 'react-google-autocomplete';
import { InputWrapper, Label, ErrorMessage } from '~/components/forms/StyledForm';
import { useEffect, useState } from 'react';
import PublicContentService from '~/api/services/tagService';
import { convertGoogleLocation } from '~/utils/LSTVUtils';

/**
 * Maps location slugs to cached names.
 */
const cachedLocationNames: Record<string, string> = {};

const AutocompleteGlobalStyles = createGlobalStyle`
  .pac-container {
    border-radius: 10px;
    margin-top: 10px;
    padding: 20px;

    .pac-item:first-of-type {
      border-top: none;
    }

    box-shadow: none;
    border: 1px solid ${(props) => props.theme.midGrey};
  }
`;

// TODO: a lot of this could get cleaned up. there's old material UI floating label stuff in here still.
const AutocompleteStyled = styled(Autocomplete)<{ $hasTextValue?: boolean; hasError?: boolean; touched?: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  border: 1px solid ${(props) => props.theme.midGrey};
  border-radius: 10px;
  background-color: ${(props) => props.theme.lightGrey};
  color: ${(props) => props.theme.black};
  font-size: 0.9375;
  padding: 12px 20px;
  box-sizing: border-box;

  & ~ label {
    /* Stop floating label from preventing clicking on form */
    pointer-events: none;
    position: absolute;
    top: 12.5px;
    left: 16px;
    margin: auto;
    z-index: ${LSTVGlobals.Z_INDEX_5_OF_100};
    transition: all 0.3s ease;
  }

  &:focus {
    border-color: ${(props) => props.theme.highlight_6_0};

    & ~ label {
      top: 0;
      color: ${(props) => props.theme.highlight_6_0};
      font-size: 0.8125rem;
    }
  }

  ${(props) =>
    Boolean(props.$hasTextValue) &&
    css`
      & ~ label {
        top: 0;
        color: ${(props) => props.theme.darkGrey};
        font-size: 0.8125rem;
      }
    `}

  ${(props) =>
    props.hasError &&
    props.touched &&
    css`
      border-color: ${props.theme.red};
      & ~ label {
        color: ${(props) => props.theme.red};
      }
    `}
`;

// TSFIXME props would be a combo of autocomplete and formik props
/**
 * Google places input for use in Formik forms
 */
const GooglePlacesSearchBar = (props: any) => {
  const { id = 'googlePlaces', $placeholder = '', name, label = '', defaultValue } = props;
  const [, meta, helpers] = useField(props);
  const [textValue, setTextValue] = React.useState('');
  return (
    <InputWrapper>
      <AutocompleteGlobalStyles />
      <Label htmlFor={name}>{label}</Label>
      <AutocompleteStyled
        id={id}
        type="string"
        onPlaceSelected={(place) => {
          setTextValue(place.formatted_address);
          helpers.setValue(place);
        }}
        options={{ types: ['(regions)'] }}
        placeholder={$placeholder}
        $hasTextValue={Boolean(textValue)}
        onChange={(e: any) => {
          setTextValue(e.target.value);
        }}
        defaultValue={defaultValue}
        autocomplete="off"
        {...props}
      />
      {meta.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
    </InputWrapper>
  );
};

/**
 * Try to make the location name look more like what react-google-autocomplete puts into the
 * input element.
 */
const normalizeName = (lstvLocation: any) => {
  let displayName: string = lstvLocation.display_name;
  if (
    lstvLocation.country_slug === 'united-states' &&
    !displayName.endsWith(', United States') &&
    !displayName.endsWith(', USA')
  ) {
    displayName = displayName + ', USA';
  }
  displayName = displayName.replace(', United States', ', USA');
  return displayName;
};

/**
 * Standalone Google places input where values are location slugs like `united-states/new-york/new-york`.
 */
export const GooglePlacesAutoComplete = ({
  placeholder,
  value,
  onChange,
  style,
}: {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  style?: CSSProperties;
}) => {
  /**
   * Slug + corresponding name. Slug present, name absent triggers retrieval of name from the server.
   * Used only when we don't have location name in {@link cachedLocationNames}, for example when
   * the user filters by location and then refreshes the page.
   */
  const [name, setName] = useState<{ slug: string; name?: string } | undefined>(undefined);

  useEffect(() => {
    if (value !== undefined && !(value in cachedLocationNames) && name?.slug !== value) {
      setName({ slug: value });
    }
  }, [value, name?.slug]);

  useEffect(() => {
    const updateName = async () => {
      const lstvLocation = await PublicContentService.getLocation(name.slug);
      setName((currrentName) =>
        currrentName.slug === name.slug ? { ...currrentName, name: normalizeName(lstvLocation) } : name
      );
    };
    if (name !== undefined && name.name === undefined) {
      updateName();
    }
  }, [name]);

  const displayedValue = value !== undefined ? cachedLocationNames[value] ?? name?.name : undefined;

  return (
    <>
      <AutocompleteGlobalStyles />
      <AutocompleteStyled
        // We're flushing AutocompleteStyled's state if the value changes.
        key={displayedValue}
        options={{ types: ['(regions)'] }}
        placeholder={placeholder}
        defaultValue={displayedValue}
        onPlaceSelected={(place) => {
          const slug = convertGoogleLocation(place);
          cachedLocationNames[slug] = place.formatted_address;
          onChange?.(slug);
        }}
        {...{ style }}
      />
    </>
  );
};

export default GooglePlacesSearchBar;
